import type { FC } from "react";

import {
  SOCIAL_PROOF_CARD_ARTICLE_CLASS,
  SOCIAL_PROOF_CARD_AVATAR_FALLBACK_CLASS,
  SOCIAL_PROOF_CARD_DEFAULTS,
  type TSocialProofCardProps,
} from "@/ui/components/data-display/Card/SocialProofCard/SocialProofCard.config";
import { Article, Container, Flex } from "@/ui/components/layout";
import { AspectRatio, Image } from "@/ui/components/primitives";
import { TextBody } from "@/ui/components/typography/TextBody";
import { TextSubtitle } from "@/ui/components/typography/TextSubtitle";

const getAuthorInitial = (name: string) => Array.from(name.trim())[0]?.toUpperCase() ?? "?";

export const SocialProofCard: FC<TSocialProofCardProps> = ({
  author,
  body,
  image,
  "data-component": component = SOCIAL_PROOF_CARD_DEFAULTS["data-component"],
}) => (
  <Article
    data-component={component}
    className={SOCIAL_PROOF_CARD_ARTICLE_CLASS}
  >
    <Container className="p-4">
      <Flex direction="column" gap="2">
        <Flex align="center" gap="2" className="text-foreground">
          { author.avatar ? (
            <Container
              data-component="SocialProofCardAvatar"
              className="relative mx-0 h-6 w-6 shrink-0 overflow-hidden rounded-full"
            >
              <Image
                { ...author.avatar }
                alt={author.avatar.alt ?? `${author.name}'s avatar`}
                fit="cover"
              />
            </Container>
          ) : (
            <Flex
              aria-hidden="true"
              data-component="SocialProofCardAvatarFallback"
              align="center"
              justify="center"
              className={SOCIAL_PROOF_CARD_AVATAR_FALLBACK_CLASS}
            >
              { getAuthorInitial(author.name) }
            </Flex>
          ) }
          <TextSubtitle
            tag="h3"
            size={3}
            isResponsive={false}
          >
            { author.name }
          </TextSubtitle>
        </Flex>
        <TextBody size={2} isResponsive={false} className="min-h-[3.9rem] line-clamp-3">
          { body }
        </TextBody>
      </Flex>
    </Container>
    <AspectRatio ratio="16:9">
      <Image { ...image } fit="cover" loading="eager" />
    </AspectRatio>
  </Article>
);

SocialProofCard.displayName = "SocialProofCard";
